import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { kr, dato, idag } from '../lib/format';
import { PageHeader, Card, Modal, Tabs } from '../components/ui';
import { Plus, Trash2, Users, Layers, Activity } from 'lucide-react';

const TRANS_TYPER = [
  { v: 'tegning', l: 'Tegning (stiftelse / nyemisjon til ny aksjonær)' },
  { v: 'overdragelse', l: 'Overdragelse (kjøp/salg mellom aksjonærer)' },
  { v: 'kapitaløkning', l: 'Kapitalforhøyelse' },
  { v: 'kapitalnedsetting', l: 'Kapitalnedsetting' },
  { v: 'utbytte', l: 'Utbytte' },
  { v: 'splitt', l: 'Aksjesplitt (multiplikator)' },
];

const Aksjeeierbok = () => {
  const [tab, setTab] = useState('eierbok');
  return (
    <>
      <PageHeader title="Aksjeeierbok"
                  subtitle="Aksjeklasser, aksjonærer og eierhistorikk" />
      <Tabs value={tab} onChange={setTab} items={[
        { key: 'eierbok', label: 'Eierbok', icon: Users },
        { key: 'aksjonaerer', label: 'Aksjonærer', icon: Users },
        { key: 'klasser', label: 'Aksjeklasser', icon: Layers },
        { key: 'transaksjoner', label: 'Transaksjoner', icon: Activity },
      ]} />
      {tab === 'eierbok' && <Eierbok />}
      {tab === 'aksjonaerer' && <Aksjonaerer />}
      {tab === 'klasser' && <Klasser />}
      {tab === 'transaksjoner' && <Transaksjoner />}
    </>
  );
};

// ── Eierbok (matrise) ──────────────────────────────────────────────────────
const Eierbok = () => {
  const [rader, setRader] = useState(null);
  const [perDato, setPerDato] = useState('');
  useEffect(() => {
    api.get('/aksjer/eierbok', { params: perDato ? { per_dato: perDato } : {} })
       .then((r) => setRader(r.data));
  }, [perDato]);
  if (!rader) return <span className="fm-text-muted">Laster…</span>;
  return (
    <>
      <Card style={{ marginBottom: 12, padding: 12 }}>
        <label>Per dato (la stå tom for nå)</label>
        <input type="date" value={perDato} onChange={(e) => setPerDato(e.target.value)}
               style={{ maxWidth: 200 }} />
      </Card>
      <Card style={{ padding: 0 }}>
        {rader.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center' }}>
            <Users size={32} className="fm-text-muted" />
            <p className="fm-text-muted" style={{ fontSize: 13 }}>
              Ingen eiere registrert. Opprett aksjeklasser, aksjonærer og en
              tegnings-transaksjon for å begynne.
            </p>
          </div>
        ) : (
          <table>
            <thead><tr>
              <th>Aksjonær</th><th>Fnr/Orgnr</th><th>Klasse</th>
              <th style={{ textAlign: 'right' }}>Antall aksjer</th>
              <th style={{ textAlign: 'right' }}>Eierandel</th>
            </tr></thead>
            <tbody>
              {rader.map((r, i) => (
                <tr key={i}>
                  <td><b>{r.aksjonaer_navn}</b></td>
                  <td className="fm-text-muted" style={{ fontSize: 11 }}>{r.fnr_orgnr || '—'}</td>
                  <td>{r.klasse_navn}</td>
                  <td style={{ textAlign: 'right' }}>{r.antall.toLocaleString('no')}</td>
                  <td style={{ textAlign: 'right' }}>{r.andel_prosent.toFixed(2)} %</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
};

// ── Aksjonærer ─────────────────────────────────────────────────────────────
const tomA = () => ({ navn: '', type: 'person', fnr_orgnr: '', adresse: '',
  postnr: '', sted: '', land: 'Norge', epost: '', telefon: '', notater: '' });
const Aksjonaerer = () => {
  const [liste, setListe] = useState(null);
  const [open, setOpen] = useState(false);
  const [red, setRed] = useState(null);
  const [s, setS] = useState(tomA());
  const last = () => api.get('/aksjer/aksjonaerer').then((r) => setListe(r.data));
  useEffect(() => { last(); }, []);
  const lagre = async () => {
    if (!s.navn) { alert('Navn må fylles inn.'); return; }
    if (red) await api.put(`/aksjer/aksjonaerer/${red.id}`, s);
    else await api.post('/aksjer/aksjonaerer', s);
    setOpen(false); last();
  };
  const slett = async (a) => {
    if (!window.confirm(`Slette ${a.navn}?`)) return;
    await api.delete(`/aksjer/aksjonaerer/${a.id}`); last();
  };
  if (!liste) return <span className="fm-text-muted">Laster…</span>;
  return (
    <>
      <Card style={{ marginBottom: 12, padding: 12 }}>
        <div className="flex justify-between items-center">
          <span className="fm-text-muted" style={{ fontSize: 12 }}>{liste.length} aksjonærer</span>
          <button className="btn-primary" onClick={() => { setRed(null); setS(tomA()); setOpen(true); }}>
            <Plus size={14} /> Ny aksjonær
          </button>
        </div>
      </Card>
      <Card style={{ padding: 0 }}>
        <table>
          <thead><tr>
            <th>Navn</th><th>Type</th><th>Fnr/Orgnr</th><th>E-post</th><th></th>
          </tr></thead>
          <tbody>
            {liste.map((a) => (
              <tr key={a.id}>
                <td><a onClick={() => { setRed(a); setS({ ...a }); setOpen(true); }}
                       style={{ cursor: 'pointer', color: 'var(--theme-accent)' }}>{a.navn}</a></td>
                <td>{a.type}</td>
                <td className="fm-text-muted" style={{ fontSize: 11 }}>{a.fnr_orgnr || '—'}</td>
                <td>{a.epost || '—'}</td>
                <td><button className="btn-ghost" onClick={() => slett(a)}>
                  <Trash2 size={14} style={{ color: 'var(--theme-danger)' }} />
                </button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      <Modal open={open} onClose={() => setOpen(false)} width={620}
             title={red ? 'Rediger aksjonær' : 'Ny aksjonær'}
             footer={<>
               <button className="btn-ghost" onClick={() => setOpen(false)}>Avbryt</button>
               <button className="btn-primary" onClick={lagre}>Lagre</button>
             </>}>
        <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div><label>Navn *</label>
            <input value={s.navn} onChange={(e) => setS({ ...s, navn: e.target.value })} /></div>
          <div><label>Type</label>
            <select value={s.type} onChange={(e) => setS({ ...s, type: e.target.value })}>
              <option value="person">Person</option>
              <option value="foretak">Foretak</option>
            </select></div>
          <div><label>{s.type === 'person' ? 'Fødselsnummer' : 'Organisasjonsnummer'}</label>
            <input value={s.fnr_orgnr} onChange={(e) => setS({ ...s, fnr_orgnr: e.target.value })} /></div>
          <div><label>E-post</label>
            <input value={s.epost} onChange={(e) => setS({ ...s, epost: e.target.value })} /></div>
          <div><label>Telefon</label>
            <input value={s.telefon} onChange={(e) => setS({ ...s, telefon: e.target.value })} /></div>
          <div><label>Land</label>
            <input value={s.land} onChange={(e) => setS({ ...s, land: e.target.value })} /></div>
          <div style={{ gridColumn: '1 / -1' }}><label>Adresse</label>
            <input value={s.adresse} onChange={(e) => setS({ ...s, adresse: e.target.value })} /></div>
          <div><label>Postnr</label>
            <input value={s.postnr} onChange={(e) => setS({ ...s, postnr: e.target.value })} /></div>
          <div><label>Sted</label>
            <input value={s.sted} onChange={(e) => setS({ ...s, sted: e.target.value })} /></div>
          <div style={{ gridColumn: '1 / -1' }}><label>Notater</label>
            <textarea rows={2} value={s.notater} onChange={(e) => setS({ ...s, notater: e.target.value })} /></div>
        </div>
      </Modal>
    </>
  );
};

// ── Aksjeklasser ───────────────────────────────────────────────────────────
const Klasser = () => {
  const [liste, setListe] = useState(null);
  const [open, setOpen] = useState(false);
  const [s, setS] = useState({ navn: 'A', paalydende: 1, stemmer_pr_aksje: 1, rettigheter: '' });
  const last = () => api.get('/aksjer/klasser').then((r) => setListe(r.data));
  useEffect(() => { last(); }, []);
  const lagre = async () => {
    await api.post('/aksjer/klasser', s); setOpen(false); last();
  };
  const slett = async (k) => {
    if (!window.confirm(`Slette klasse ${k.navn}?`)) return;
    await api.delete(`/aksjer/klasser/${k.id}`); last();
  };
  if (!liste) return <span className="fm-text-muted">Laster…</span>;
  return (
    <>
      <Card style={{ marginBottom: 12, padding: 12 }}>
        <div className="flex justify-between items-center">
          <span className="fm-text-muted" style={{ fontSize: 12 }}>{liste.length} klasser</span>
          <button className="btn-primary" onClick={() => setOpen(true)}><Plus size={14} /> Ny klasse</button>
        </div>
      </Card>
      <Card style={{ padding: 0 }}>
        <table>
          <thead><tr>
            <th>Navn</th><th style={{ textAlign: 'right' }}>Pålydende</th>
            <th style={{ textAlign: 'right' }}>Stemmer/aksje</th>
            <th>Rettigheter</th><th></th>
          </tr></thead>
          <tbody>
            {liste.map((k) => (
              <tr key={k.id}>
                <td><b>{k.navn}</b></td>
                <td style={{ textAlign: 'right' }}>{kr(k.paalydende)}</td>
                <td style={{ textAlign: 'right' }}>{k.stemmer_pr_aksje}</td>
                <td className="fm-text-muted" style={{ fontSize: 12 }}>{k.rettigheter || '—'}</td>
                <td><button className="btn-ghost" onClick={() => slett(k)}>
                  <Trash2 size={14} style={{ color: 'var(--theme-danger)' }} />
                </button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      <Modal open={open} onClose={() => setOpen(false)} title="Ny aksjeklasse" width={520}
             footer={<>
               <button className="btn-ghost" onClick={() => setOpen(false)}>Avbryt</button>
               <button className="btn-primary" onClick={lagre}>Lagre</button>
             </>}>
        <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div><label>Navn *</label>
            <input value={s.navn} onChange={(e) => setS({ ...s, navn: e.target.value })} /></div>
          <div><label>Pålydende (kr)</label>
            <input type="number" step="0.01" value={s.paalydende}
                   onChange={(e) => setS({ ...s, paalydende: parseFloat(e.target.value || 0) })} /></div>
          <div><label>Stemmer pr aksje</label>
            <input type="number" step="0.01" value={s.stemmer_pr_aksje}
                   onChange={(e) => setS({ ...s, stemmer_pr_aksje: parseFloat(e.target.value || 0) })} /></div>
          <div style={{ gridColumn: '1 / -1' }}><label>Rettigheter / vedtekter</label>
            <textarea rows={3} value={s.rettigheter}
                      onChange={(e) => setS({ ...s, rettigheter: e.target.value })} /></div>
        </div>
      </Modal>
    </>
  );
};

// ── Transaksjoner ──────────────────────────────────────────────────────────
const tomT = () => ({ dato: idag(), type: 'tegning', klasse_id: '', fra_aksjonaer_id: '',
  til_aksjonaer_id: '', antall: 0, pris_pr_aksje: 0, kommentar: '' });
const Transaksjoner = () => {
  const [liste, setListe] = useState(null);
  const [klasser, setKlasser] = useState([]);
  const [aksjonaerer, setAksjonaerer] = useState([]);
  const [open, setOpen] = useState(false);
  const [s, setS] = useState(tomT());
  const last = () => api.get('/aksjer/transaksjoner').then((r) => setListe(r.data));
  useEffect(() => {
    last();
    api.get('/aksjer/klasser').then((r) => setKlasser(r.data));
    api.get('/aksjer/aksjonaerer').then((r) => setAksjonaerer(r.data));
  }, []);
  const lagre = async () => {
    if (!s.klasse_id) { alert('Velg aksjeklasse.'); return; }
    await api.post('/aksjer/transaksjoner', s); setOpen(false); last();
  };
  const slett = async (t) => {
    if (!window.confirm('Slette denne transaksjonen? (Eierboken blir omberegnet.)')) return;
    await api.delete(`/aksjer/transaksjoner/${t.id}`); last();
  };
  const navn = (id) => aksjonaerer.find((a) => a.id === id)?.navn || '—';
  const klNavn = (id) => klasser.find((k) => k.id === id)?.navn || '—';
  if (!liste) return <span className="fm-text-muted">Laster…</span>;
  return (
    <>
      <Card style={{ marginBottom: 12, padding: 12 }}>
        <div className="flex justify-between items-center">
          <span className="fm-text-muted" style={{ fontSize: 12 }}>{liste.length} transaksjoner</span>
          <button className="btn-primary" onClick={() => { setS(tomT()); setOpen(true); }}>
            <Plus size={14} /> Ny transaksjon
          </button>
        </div>
      </Card>
      <Card style={{ padding: 0 }}>
        <table>
          <thead><tr>
            <th>Dato</th><th>Type</th><th>Klasse</th><th>Fra</th><th>Til</th>
            <th style={{ textAlign: 'right' }}>Antall</th>
            <th style={{ textAlign: 'right' }}>Pris/aksje</th>
            <th style={{ textAlign: 'right' }}>Sum</th>
            <th></th>
          </tr></thead>
          <tbody>
            {liste.map((t) => (
              <tr key={t.id}>
                <td>{dato(t.dato)}</td>
                <td><span className="badge badge-neutral">{t.type}</span></td>
                <td>{klNavn(t.klasse_id)}</td>
                <td>{t.fra_aksjonaer_id ? navn(t.fra_aksjonaer_id) : '—'}</td>
                <td>{t.til_aksjonaer_id ? navn(t.til_aksjonaer_id) : '—'}</td>
                <td style={{ textAlign: 'right' }}>{t.antall.toLocaleString('no')}</td>
                <td style={{ textAlign: 'right' }}>{kr(t.pris_pr_aksje)}</td>
                <td style={{ textAlign: 'right' }}>{kr(t.sum_belop)}</td>
                <td><button className="btn-ghost" onClick={() => slett(t)}>
                  <Trash2 size={14} style={{ color: 'var(--theme-danger)' }} />
                </button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      <Modal open={open} onClose={() => setOpen(false)} title="Ny aksjetransaksjon" width={620}
             footer={<>
               <button className="btn-ghost" onClick={() => setOpen(false)}>Avbryt</button>
               <button className="btn-primary" onClick={lagre}>Lagre</button>
             </>}>
        <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div><label>Dato</label>
            <input type="date" value={s.dato} onChange={(e) => setS({ ...s, dato: e.target.value })} /></div>
          <div><label>Type</label>
            <select value={s.type} onChange={(e) => setS({ ...s, type: e.target.value })}>
              {TRANS_TYPER.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select></div>
          <div><label>Aksjeklasse *</label>
            <select value={s.klasse_id} onChange={(e) => setS({ ...s, klasse_id: parseInt(e.target.value) || '' })}>
              <option value="">— velg —</option>
              {klasser.map((k) => <option key={k.id} value={k.id}>{k.navn}</option>)}
            </select></div>
          <div><label>Antall aksjer</label>
            <input type="number" step="1" value={s.antall}
                   onChange={(e) => setS({ ...s, antall: parseFloat(e.target.value || 0) })} /></div>
          <div><label>Fra aksjonær (selger)</label>
            <select value={s.fra_aksjonaer_id}
                    onChange={(e) => setS({ ...s, fra_aksjonaer_id: parseInt(e.target.value) || '' })}>
              <option value="">— (ingen / nyemisjon) —</option>
              {aksjonaerer.map((a) => <option key={a.id} value={a.id}>{a.navn}</option>)}
            </select></div>
          <div><label>Til aksjonær (kjøper)</label>
            <select value={s.til_aksjonaer_id}
                    onChange={(e) => setS({ ...s, til_aksjonaer_id: parseInt(e.target.value) || '' })}>
              <option value="">— (ingen / sletting) —</option>
              {aksjonaerer.map((a) => <option key={a.id} value={a.id}>{a.navn}</option>)}
            </select></div>
          <div><label>Pris pr aksje</label>
            <input type="number" step="0.01" value={s.pris_pr_aksje}
                   onChange={(e) => setS({ ...s, pris_pr_aksje: parseFloat(e.target.value || 0) })} /></div>
          <div><label>Sum (overstyr)</label>
            <input type="number" step="0.01" value={s.sum_belop || ''}
                   placeholder={(s.antall * s.pris_pr_aksje).toFixed(2)}
                   onChange={(e) => setS({ ...s, sum_belop: parseFloat(e.target.value || 0) })} /></div>
          <div style={{ gridColumn: '1 / -1' }}><label>Kommentar</label>
            <textarea rows={2} value={s.kommentar}
                      onChange={(e) => setS({ ...s, kommentar: e.target.value })} /></div>
        </div>
      </Modal>
    </>
  );
};

export default Aksjeeierbok;
